import React, { useState } from "react";
import Editor from "@monaco-editor/react";
import "../CodeConverter.css";
import { FaDownload, FaCompress, FaBrush, FaCopy, FaCheck, FaTrash } from "react-icons/fa";
import JsonEditorPanel from "../components/JsonEditorPanel";
import CommonSeoContent from "../components/CommonSeoContent";
import useEditorActions from "../utils/useEditorActions";
import { Helmet } from "react-helmet";

const JsonToGo = () => {
  const [inputCode, setInputCode] = useState("");
  const [outputCode, setOutputCode] = useState("");

  // Import reusable actions
  const { handleDownload, handleClear } = useEditorActions(
    setInputCode,
    outputCode,
    setOutputCode
  );

  const toGoStruct = (name, obj, indent = "") => {
    let structLines = [`type ${name} struct {`];
    let nestedStructs = [];

    for (const key in obj) {
      const value = obj[key];
      let goType = inferType(value);

      if (goType === "struct") {
        const structName = `${name}_${capitalize(key)}`;
        nestedStructs.push(toGoStruct(structName, value, indent + "\t"));
        structLines.push(`${indent}\t${capitalize(key)} ${structName} \`json:"${key}"\``);
      } else {
        structLines.push(`${indent}\t${capitalize(key)} ${goType} \`json:"${key}"\``);
      }
    }

    structLines.push(`${indent}}`);

    return [...nestedStructs, ...structLines].join("\n");
  };

  const inferType = (value) => {
    if (Array.isArray(value)) {
      if (value.length === 0) return "[]interface{}";
      return "[]" + inferType(value[0]);
    }
    if (typeof value === "string") return "string";
    if (typeof value === "number") return Number.isInteger(value) ? "int" : "float64";
    if (typeof value === "boolean") return "bool";
    if (typeof value === "object" && value !== null) return "struct";
    return "interface{}";
  };

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const handleConvert = () => {
    try {
      const json = JSON.parse(inputCode);
      if (typeof json !== "object") throw new Error("Root must be an object");
      const goStruct = toGoStruct("AutoGenerated", json);
      setOutputCode(goStruct);
    } catch (e) {
      setOutputCode("// ❌ Invalid JSON:\n" + e.message);
    }
  };

  return (
    <div className="formatter-container">
      <h1>JSON ➝ Go Struct Converter</h1>

      {/* Toolbar */}
      <div className="toolbar">
        <button onClick={handleConvert} className="btn primary">
          <FaBrush /> Convert
        </button>
        <button onClick={() => handleDownload("go", "go-struct")} className="btn success">
          <FaDownload /> Download
        </button>
        <button onClick={handleClear} className="btn danger">
          <FaTrash /> Clear
        </button>
      </div>

      {/* Editors */}
      <div className="editor-panels">
        <JsonEditorPanel title="Input JSON" language="json" code={inputCode} setCode={setInputCode} />
        <div className="editor-section-empty-div"></div>
        <JsonEditorPanel title="Output Go Struct" language="go" code={outputCode} />
      </div>
	  
	  <CommonSeoContent
		  title="Best Online JSON to Go Struct Converter"
		  description="Convert JSON data into Go struct code instantly with jsonconverter.org. Supports nested JSON and produces clean Go code for developers."
		  keywords="JSON to Go, JSON to Go Struct, Go struct generator, json to code converter, online JSON converter, jsonconverter.org"
		  author="jsonconverter.org"
		  heading="Why Use JSON to Go Struct Converter?"
		  whyUseText="Our JSON to Go Converter helps you generate idiomatic Go struct definitions from JSON data in seconds. Ideal for working with APIs, microservices, or file parsing in Go applications, this tool supports embedded structures, arrays, optional fields, and correct Go types like `string`, `int`, `bool`, and `[]Type`. It automatically formats field names using PascalCase and includes helpful struct tags for JSON serialization. Fully browser-based — your data never leaves your computer. It's fast, accurate, and made for Go developers."
		  howToUseSteps={[
			"Paste your JSON in the input editor.",
			"Click Convert.",
			"View and copy your Go struct output instantly.",
		  ]}
		  features={[
			"Supports nested JSON objects and arrays",
			"Generates Go code with JSON tags",
			"No sign-up required",
			"Free to use",
		  ]}
		  inputTitle="Input JSON:"
		  inputExample={`{
		  "name": "John Doe",
		  "age": 30,
		  "emails": ["john@example.com"],
		  "isActive": true,
		  "profile": {
			"bio": "Developer",
			"website": "https://jsonconverter.org"
		  }
		}`}
		  outputTitle="Output Go Struct:"
		  outputExample={`type AutoGenerated struct {
		  Name     string        \`json:"name"\`
		  Age      int           \`json:"age"\`
		  Emails   []string      \`json:"emails"\`
		  IsActive bool          \`json:"isActive"\`
		  Profile  AutoGenerated_Profile \`json:"profile"\`
		}

		type AutoGenerated_Profile struct {
		  Bio     string \`json:"bio"\`
		  Website string \`json:"website"\`
		}`}
		  callToAction="Start converting your JSON to Go structs instantly — no signup required."
		/>  
    </div>
  );
};

export default JsonToGo;
